import { Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import type { ModuleManifest, TenantSettings } from "@praesto/contracts";
import { AppIcon, ToastProvider } from "@praesto/ui-kit";
import { useIsDesktop } from "../lib/use-is-desktop";
import { useManifests } from "../lib/manifests-context";
import { CommandRegistryProvider } from "@praesto/core";
import { useActiveHotel, useCore } from "../lib/core";
import shellLocaleIt from "../locales/it.json";
import helpLocaleIt from "@praesto/help/locales/it.json";
import { HelpProvider } from "../help/HelpHost";
import { HelpButton } from "../help/HelpButton";
import { OScroll } from "../components/OScroll";
import { useBuiltInCommands } from "../lib/built-in-commands";
import { Topbar } from "../components/Topbar";
import { ModuleSidebar } from "../components/ModuleSidebar";
import { SubNavSidebar } from "../components/SubNavSidebar";
import { MobileTopBar } from "../components/MobileTopBar";
import { MobileBottomNav } from "../components/MobileBottomNav";
import { MobileSubNavDrawer } from "../components/MobileSubNavDrawer";
import { MobileMoreDrawer } from "../components/MobileMoreDrawer";
import { CommandPalette } from "../components/CommandPalette";
import { ImpersonationBanner } from "../components/ImpersonationBanner";
import { ForegroundPushToast } from "../components/ForegroundPushToast";
import { AssistantFab } from "../components/AssistantFab";
import { AssistantOverlay } from "../components/AssistantOverlay";
import { OnboardingGuard } from "../onboarding/OnboardingGuard";
import { SampleDataBanner } from "../onboarding/SampleDataBanner";
import { TrialNotice } from "../onboarding/TrialNotice";

/**
 * Top-level shell. Switches between DesktopShell (≥768px) and MobileShell
 * (<768px) reactively. Both shells share the same router Outlet.
 *
 * Manifests come from the `ManifestsProvider` set up in App.tsx after
 * Boot resolves — no async fetch here, so the sidebar can't flicker
 * empty between mount and the effect.
 *
 * CommandRegistryProvider wraps everything so any descendant can register
 * commands (built-in + module-specific in V2).
 */
export function AppLayout() {
  const manifests = useManifests();
  const location = useLocation();
  const [assistantOpen, setAssistantOpen] = useState(false);

  // On the assistant's own page the FAB is a shortcut to where you already
  // are — and with the composer at the bottom edge it covered the send button
  // (C3F v18, AppShell reference).
  const onAssistantPage = location.pathname.includes("/procedure/assistant");

  // During the mandatory first-run wizard, suppress every "escape hatch" the
  // shell offers besides the wizard itself — not just the sidebars (see
  // OnboardingShell) but also the ⌘K command palette and the AI assistant,
  // both of which can navigate away from / obscure the wizard.
  //
  // Derived from the onboarding STATE, not from the pathname: the guide is an
  // allowed detour (`/help`), and reading the path alone made the palette and
  // the assistant reappear the moment the owner opened it — the very flicker
  // this suppression exists to avoid.
  const isOnboarding = useIsOnboarding();

  return (
    // ToastProvider wraps the whole authenticated shell so every module route
    // (shifts week/availability/create, etc.) can call ui-kit `useToast`. It
    // used to live in the now-removed legacy AppShell; the live TanStack-router
    // shell never re-mounted it, which crashed every toast-using route with
    // "useToast must be used within a <ToastProvider>".
    <ToastProvider>
      <ForegroundPushToast />
      <CommandRegistryProvider>
        <ShellTranslationsRegistrar />
        <OnboardingGuard />
        <BuiltInsRegistrar manifests={manifests} />
        {/* HelpProvider wraps the shell — and deliberately stays mounted during
            the onboarding wizard, unlike the palette and the assistant below.
            A brand-new owner is the reader who needs the guide most, and the
            panel is read-only: it cannot navigate away or leave the account
            half-configured. */}
        <HelpProvider>
          <ShellSwitcher manifests={manifests} />
        </HelpProvider>
        {!isOnboarding && <CommandPalette />}
        {!isOnboarding && (
          <>
            {!onAssistantPage && (
              <AssistantFab manifests={manifests} onOpen={() => setAssistantOpen(true)} />
            )}
            <AssistantOverlay
              manifests={manifests}
              open={assistantOpen}
              onClose={() => setAssistantOpen(false)}
            />
          </>
        )}
      </CommandRegistryProvider>
    </ToastProvider>
  );
}

/**
 * Registers the host-owned shell translation strings (bottom nav, drawers,
 * command palette) into the shared core i18n instance so the shell chrome
 * never hardcodes user-facing Italian in TSX. Idempotent.
 *
 * The bundle is keyed under the `shell.*` namespace prefix so it cannot
 * collide with any module bundle; `registerModuleTranslations` merges every
 * bundle into one shared namespace and only uses the first argument as a
 * label, so reusing the "home" module id (the host surface) is safe here —
 * `ModuleId` has no dedicated "shell" member and contracts are out of scope.
 */
function ShellTranslationsRegistrar() {
  const core = useCore();
  // Lazy initializer runs exactly once per mount, before first paint, and
  // never re-runs on re-render — a safe place for the idempotent registration.
  useState(() => {
    core.i18n.registerModuleTranslations("home", { it: shellLocaleIt, en: {} });
    // The guide ships its own strings (help.*), registered the same way so the
    // shell chrome and the guide share one translation namespace.
    core.i18n.registerModuleTranslations("home", { it: helpLocaleIt, en: {} });
    return null;
  });
  return null;
}

/**
 * The scrolling main region, with overlay scrollbars.
 *
 * `min-h-full` on the content keeps short pages filling the viewport, which the
 * old `overflow-auto` main did for free.
 */
function OScrollMain({ children, mobile = false }: { children: ReactNode; mobile?: boolean }) {
  return (
    <OScroll
      className="flex-1"
      aria-label="Contenuto"
      contentClassName={
        mobile ? "min-h-full pb-bottom-nav flex flex-col" : "min-h-full flex flex-col"
      }
    >
      <main className="flex min-h-full flex-1 flex-col [&>*]:w-full">{children}</main>
    </OScroll>
  );
}

/**
 * True while the owner still has to finish the first-run wizard — the same
 * condition `OnboardingGuard` redirects on.
 */
function useIsOnboarding(): boolean {
  const core = useCore();
  const [settings, setSettings] = useState<TenantSettings | null>(() => {
    try {
      return core.tenant.settings();
    } catch {
      return null;
    }
  });
  useEffect(() => core.tenant.onSettingsChange(setSettings), [core]);
  return core.rbac.can("manage", "hotel") && settings?.onboarding?.status === "pending";
}

function BuiltInsRegistrar({ manifests }: { manifests: ReadonlyArray<ModuleManifest> }) {
  useBuiltInCommands(manifests);
  return null;
}

function ShellSwitcher({ manifests }: { manifests: ReadonlyArray<ModuleManifest> }) {
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const activeModuleId = location.pathname.split("/")[2];
  const activeModule = manifests.find((m) => m.id === activeModuleId) ?? null;

  // Mandatory first-run wizard = a full-screen takeover. The owner must NOT be
  // able to navigate away from it via the shell chrome (sidebars, bottom nav,
  // command palette) — otherwise clicking a nav item bounces off `OnboardingGuard`
  // (which redirects back) in a jarring flash. Only a minimal header (brand +
  // sign-out) remains; the sample-data banner is likewise suppressed here.
  if (activeModuleId === "onboarding") {
    return <OnboardingShell />;
  }

  if (isDesktop) {
    // C3F v18 shell: the Night rail runs FULL HEIGHT and the topbar belongs to
    // the content column (white on Paper), covering sub-bar + content but NOT
    // the rail. With the bar above everything the rail ends up truncated and
    // the two dark masses never touch — a deliberate deviation from the old
    // layout, specified by design/system (AppShell reference, "do not correct
    // on next sync"). Banners live in the content column for the same reason.
    return (
      <div className="flex h-[100dvh] overflow-hidden bg-canvas text-fg-default">
        <ModuleSidebar manifests={manifests} />
        <div className="flex min-w-0 flex-1 flex-col">
          <ImpersonationBanner />
          <TrialNotice />
          <SampleDataBanner />
          <Topbar />
          <div className="flex flex-1 overflow-hidden">
            <SubNavSidebar activeModule={activeModule} />
            {/* The percentage chain (`min-h-full` on descendants) dies inside
                the scroll area: the contents element's `height` stays auto, so
                a child's min-h-full resolves to nothing and section-level
                sidebars stop at content height. Flex used-heights fix it once
                for every route: contents column → main grows → route roots can
                `flex-1` to reach the bottom edge (the sub-rail of Persone /
                Impostazioni was ending mid-screen). */}
            <OScroll
              className="flex-1"
              aria-label="Contenuto"
              contentClassName="min-h-full flex flex-col"
            >
              <main className="flex min-h-full flex-1 flex-col [&>*]:w-full">
                <Outlet />
              </main>
            </OScroll>
          </div>
        </div>
      </div>
    );
  }

  return <MobileShell manifests={manifests} activeModule={activeModule} />;
}

/**
 * Full-screen layout for the mandatory first-run wizard: a minimal header
 * (brand + structure name + sign-out) and the wizard content, with NO
 * sidebars, bottom nav, or command palette. The owner must complete the wizard
 * (or sign out) — they cannot navigate away from it via the shell chrome. Same
 * on desktop and mobile.
 *
 * The header names the structure the owner typed at signup, never the tenant
 * id from the URL: that id is technical and means nothing to the customer.
 */
export function OnboardingShell() {
  const core = useCore();
  const { hotel } = useActiveHotel();
  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-canvas text-fg-default">
      <ImpersonationBanner />
      <header className="h-11 bg-topbar-bg text-topbar-fg border-b border-border-default flex items-center px-3 gap-3 z-10 flex-shrink-0">
        {/* The brand arrives as a file, never a recomposition (the old green
            dot + mono "Praesto" was the pre-v18 identity). Below 34px the
            wordmark is illegible, so the app icon carries the mark here. */}
        <AppIcon size={28} />
        {hotel && (
          <span className="text-caption text-fg-muted truncate max-w-[200px]">{hotel.name}</span>
        )}
        {/* The one escape hatch kept during the wizard: an owner stuck on the
            first step needs to be able to read what the wizard is asking for. */}
        <div className="ml-auto flex items-center gap-2">
          <HelpButton />
          <button
            type="button"
            onClick={() => void core.auth.signOut()}
            className="text-caption text-fg-muted hover:text-fg-default"
          >
            Esci
          </button>
        </div>
      </header>
      <OScrollMain>
        <Outlet />
      </OScrollMain>
    </div>
  );
}

function MobileShell({
  manifests,
  activeModule,
}: {
  manifests: ReadonlyArray<ModuleManifest>;
  activeModule: ModuleManifest | null;
}) {
  const location = useLocation();
  const [subNavOpen, setSubNavOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Close drawers on route change
  useEffect(() => {
    setSubNavOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-canvas text-fg-default">
      <ImpersonationBanner />
      <TrialNotice />
      <SampleDataBanner />
      <MobileTopBar activeModule={activeModule} onOpenSubNav={() => setSubNavOpen(true)} />
      <OScrollMain mobile>
        <Outlet />
      </OScrollMain>
      <MobileBottomNav manifests={manifests} onOpenMore={() => setMoreOpen(true)} />
      <MobileSubNavDrawer
        activeModule={activeModule}
        open={subNavOpen}
        onClose={() => setSubNavOpen(false)}
      />
      <MobileMoreDrawer open={moreOpen} onClose={() => setMoreOpen(false)} />
    </div>
  );
}
